// ─── Post-generation visual checklist ──────────────────────────────────────
// Confirms to the user what actually shipped in a just-generated chapter.
//
// Derives "delivered" from composeThesisDocument — the same scan the real
// DOCX/PDF export runs — so this can never disagree with what the user's
// thesis actually contains. Never re-parse the tokens separately here.

import { composeThesisDocument } from './exportCompositionEngine.js'
import { VISUAL_LEVELS, clampVisualLevel } from './visualLevels.js'

/**
 * @param {{ html: string, chapterNumber: number, level: number }} params
 * @returns {{
 *   expected: {figures:number, tables:number},
 *   delivered: {figures:number, tables:number},
 *   figures: Array<{label:string, title:string, ok:boolean, problem:string|null}>,
 *   tables:  Array<{label:string, title:string, ok:boolean, problem:string|null}>,
 *   complete: boolean,
 *   issues: string[],
 * }}
 */
export function buildVisualChecklist({ html, chapterNumber, level }) {
  const expected = VISUAL_LEVELS[clampVisualLevel(level)]
  const composed = composeThesisDocument({
    thesis: {},
    chapters: [{ chapter_number: chapterNumber || 1, content: html || '' }],
  })

  const checkItem = (entry, kind) => {
    if (!entry.code || !entry.code.trim()) return `${kind} body missing — will render as an empty placeholder box`
    if (!entry.title || !entry.title.trim()) return `${kind} has no title`
    if (!entry.caption || !entry.caption.trim()) return `${kind} has no caption`
    return null
  }

  const figures = [...composed.figureRegistry.values()].map((f) => {
    const problem = checkItem(f, 'Figure')
    return { label: f.displayLabel, title: f.title || '(untitled)', ok: !problem, problem }
  })
  const tables = [...composed.tableRegistry.values()].map((t) => {
    const problem = checkItem(t, 'Table')
    return { label: t.displayLabel, title: t.title || '(untitled)', ok: !problem, problem }
  })

  const delivered = { figures: figures.length, tables: tables.length }

  const issues = []
  if (delivered.figures < expected.figures) {
    issues.push(`Planned ${expected.figures} figure${expected.figures === 1 ? '' : 's'}, only ${delivered.figures} generated`)
  }
  if (delivered.tables < expected.tables) {
    issues.push(`Planned ${expected.tables} table${expected.tables === 1 ? '' : 's'}, only ${delivered.tables} generated`)
  }
  for (const f of figures) if (f.problem) issues.push(`${f.label}: ${f.problem}`)
  for (const t of tables) if (t.problem) issues.push(`${t.label}: ${t.problem}`)

  return {
    expected,
    delivered,
    figures,
    tables,
    complete: issues.length === 0,
    issues,
  }
}
